import { useState } from 'react';
import { EXIT_KINDS, formatCost, formatMoney } from './costing';

export type StockMovementFields = { reason: string; note: string; exitType?: string; reference?: string };

type Props = {
    // "+" ajuste de entrada · "−" salida · lápiz: conteo de un producto (reemplaza la cantidad)
    mode: 'add' | 'remove' | 'replace';
    productName: string;
    quantity: number;
    previous?: number;
    averageCost?: number | null;
    saving?: boolean;
    onConfirm: (fields: StockMovementFields) => void;
    onClose: () => void;
};

const TITLES = { add: 'Agregar al inventario', remove: 'Sacar del inventario', replace: 'Ajustar inventario' };

// Motivo (y tipo de salida) antes de guardar un cambio de stock. Los movimientos salen o entran al costo promedio.
export const StockMovementDialog = ({ mode, productName, quantity, previous, averageCost, saving = false, onConfirm, onClose }: Props) => {
    const [reason, setReason] = useState('');
    const [exitType, setExitType] = useState(EXIT_KINDS[0].value);
    const [reference, setReference] = useState('');
    const [showErrors, setShowErrors] = useState(false);

    const confirm = () => {
        if (!reason.trim()) {
            setShowErrors(true);
            return;
        }
        const fields: StockMovementFields = { reason: reason.trim(), note: reason.trim() };
        if (mode === 'remove') {
            fields.exitType = exitType;
            if (exitType === 'DEVOLUCION_PROVEEDOR' && reference.trim()) fields.reference = reference.trim();
        }
        onConfirm(fields);
    };

    const diff = mode === 'replace' ? quantity - (previous ?? 0) : mode === 'remove' ? -quantity : quantity;
    const average = averageCost === null || averageCost === undefined ? null : Number(averageCost);

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }} tabIndex={-1} role="dialog" aria-label={TITLES[mode]}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{TITLES[mode]}</h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" disabled={saving}></button>
                    </div>
                    <div className="modal-body">
                        <p className="mb-3">
                            <strong>{productName}</strong>
                            <br />
                            <span className="text-muted small">
                                {mode === 'replace'
                                    ? `De ${previous} a ${quantity} (${diff >= 0 ? '+' : '-'}${Math.abs(diff)})`
                                    : `${mode === 'add' ? 'Ingreso' : 'Salida'} de ${quantity}`}
                                {average !== null && diff !== 0 ? ` · al costo promedio ${formatCost(average)} = ${formatMoney(Math.abs(diff) * average)}` : ''}
                            </span>
                        </p>
                        {mode === 'remove' && (
                            <div className="mb-3">
                                <label htmlFor="exitTypeSelect" className="form-label">Tipo de salida *</label>
                                <select id="exitTypeSelect" className="form-select" value={exitType} disabled={saving} onChange={(e) => setExitType(e.target.value)}>
                                    {EXIT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                                </select>
                            </div>
                        )}
                        {mode === 'remove' && exitType === 'DEVOLUCION_PROVEEDOR' && (
                            <div className="mb-3">
                                <label htmlFor="exitReferenceInput" className="form-label">Factura del proveedor</label>
                                <input id="exitReferenceInput" type="text" className="form-control" maxLength={100} value={reference}
                                    disabled={saving} onChange={(e) => setReference(e.target.value)} />
                            </div>
                        )}
                        <div className="mb-3">
                            <label htmlFor="movementReasonInput" className="form-label">Motivo *</label>
                            <input
                                id="movementReasonInput"
                                type="text"
                                className={`form-control${showErrors && !reason.trim() ? ' is-invalid' : ''}`}
                                maxLength={300}
                                placeholder={mode === 'add' ? 'Por qué entra (las compras se registran en Compras)' : 'Motivo del movimiento'}
                                value={reason}
                                disabled={saving}
                                onChange={(e) => setReason(e.target.value)}
                            />
                            <div className="invalid-feedback">Campo requerido</div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
                        <button type="button" className="btn btn-primary" onClick={confirm} disabled={saving}>
                            {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</> : 'Guardar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
